// Phase 26 Part 1: structured CSV export.
// Posts the precomputed result + scenario context to the backend, which
// returns a multi-section CSV. The blob is downloaded with the filename the
// server provides via Content-Disposition (already sanitized).
import { useState } from 'react';
import type {
  ComputeResponse,
  FleetTCOResponse,
  ScenarioComparisonResponse,
  ScenarioInput,
} from '../types';
import { exportCompareCsv, exportScenarioCsv } from '../utils/api';

type ScenarioMode = 'personal' | 'fleet';

interface ScenarioCsvProps {
  kind: 'scenario';
  scenario: ScenarioInput;
  mode: ScenarioMode;
  personalResult?: ComputeResponse | null;
  fleetResult?: FleetTCOResponse | null;
}

interface CompareCsvProps {
  kind: 'compare';
  scenarioA: ScenarioInput;
  scenarioB: ScenarioInput;
  comparison: ScenarioComparisonResponse;
  mode?: ScenarioMode;
}

type Props = (ScenarioCsvProps | CompareCsvProps) & {
  className?: string;
  /** Optional override; defaults to "Export CSV". */
  label?: string;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default function ExportCsvButton(props: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      if (props.kind === 'scenario') {
        const { blob, filename } = await exportScenarioCsv({
          scenario: props.scenario,
          mode: props.mode,
          personal_result: props.mode === 'personal' ? props.personalResult ?? null : null,
          fleet_result: props.mode === 'fleet' ? props.fleetResult ?? null : null,
        });
        downloadBlob(blob, filename);
      } else {
        const { blob, filename } = await exportCompareCsv({
          scenario_a: props.scenarioA,
          scenario_b: props.scenarioB,
          comparison: props.comparison,
          mode: props.mode ?? 'fleet',
        });
        downloadBlob(blob, filename);
      }
    } catch (e) {
      setError('Export failed — try again');
      setTimeout(() => setError(null), 4000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={props.className} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg"
        data-testid="export-csv-button"
      >
        <DownloadIcon />
        {busy ? 'Exporting…' : (props.label ?? 'Export CSV')}
      </button>
      {error && (
        <span style={{ fontSize: 12, color: 'var(--tco-warn, #b91c1c)' }} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
